import * as React from 'react';
import styled from 'styled-components';
import { generateId } from '@agilepoint/utils-common';
import { Property } from 'csstype';
import { HostableSurfaceProvider, HostableSurfaceEvents, Handler } from './hostable-surface-context';
import { useGlobalEventManager } from '../../utils';
import { CanvasLayer } from '../layers';
import { currentDocument } from '../../utils/env';

export interface IHostableSurfaceProps {
    children?: React.ReactNode;
    className?: string;
    position?: Property.Position;
    preventClickEventFromBubbling?: boolean;
}

function useElement(fallbackElement: HTMLElement): [HTMLElement, (element: HTMLElement | null) => void] {
    const [surfaceElement, setSurfaceElement] = React.useState<HTMLElement | null>(null);
    return [surfaceElement || fallbackElement, setSurfaceElement];
}

function useEventNameGenerator() {
    const surfaceId = React.useMemo(generateId, []);
    const getEventName = React.useCallback((eventName: HostableSurfaceEvents) => `${eventName}_${surfaceId}`, [surfaceId]);
    return { getEventName };
}

function HostableSurfaceBase(props: IHostableSurfaceProps) {
    const { preventClickEventFromBubbling, className, children } = props;
    const [surfaceElement, setSurfaceElement] = useElement(currentDocument.body);
    const getSurfaceClientRect = React.useCallback(() => surfaceElement.getBoundingClientRect(), [surfaceElement]);
    const { on, off, emit } = useGlobalEventManager();
    const { getEventName } = useEventNameGenerator();

    const addSurfaceEventListener = React.useCallback((eventName: HostableSurfaceEvents, handler: Handler) => { on(getEventName(eventName), handler); }, [getEventName, on]);

    const removeSurfaceEventListener = React.useCallback((eventName: HostableSurfaceEvents, handler: Handler) => { off(getEventName(eventName), handler); }, [getEventName, off]);

    const _onSurfaceClick = React.useCallback((ev: React.MouseEvent) => {
        if (preventClickEventFromBubbling) {
            ev.stopPropagation();
        }
        ev.persist();
        emit(getEventName('surfaceClick'), ev);
    }, [emit, getEventName, preventClickEventFromBubbling]);

    const _onSurfaceResize = React.useCallback((ev: React.MouseEvent) => {
        emit(getEventName('surfaceResize'), ev);
    }, [emit, getEventName]);

    React.useEffect(() => {
        const eventName = 'windowResize';
        on(eventName, _onSurfaceResize);
        return () => { off(eventName, _onSurfaceResize); };
    }, [on, off, _onSurfaceResize]);

    const contextProps = React.useMemo(() => ({
        surfaceElement,
        getSurfaceClientRect,
        addSurfaceEventListener,
        removeSurfaceEventListener
    }), [surfaceElement, getSurfaceClientRect, addSurfaceEventListener, removeSurfaceEventListener]);

    return (
        <CanvasLayer className={className} ref={setSurfaceElement} onClick={_onSurfaceClick}>
            <HostableSurfaceProvider value={contextProps}>
                {children}
            </HostableSurfaceProvider>
        </CanvasLayer>
    );
}

export const HostableSurface = styled(HostableSurfaceBase).attrs(() => ({
    className: String('ap-hostableSurface')
}))`
    position: ${({ position }) => (position ? position : 'relative')};
    height: 100%;
    width: 100%;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: 5;
`;
